import React, { useEffect } from 'react';
import { Box, Container, Typography, Grid, Card, CardContent, CardMedia } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix for default marker icons in Leaflet with React
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const RoutesSection = styled(Box)(({ theme }) => ({
  padding: '80px 0',
  background: theme.palette.background.default,
}));

const RoutesMap = styled(Box)(({ theme }) => ({
  backgroundColor: '#181818',
  padding: '30px',
  borderRadius: '8px',
  marginTop: '40px',
  '& .leaflet-container': {
    height: '400px',
    width: '100%',
    marginBottom: '32px',
  },
}));

const RouteCard = styled(Card)(({ theme }) => ({
  height: '100%',
  padding: '20px',
  borderRadius: '8px',
  boxShadow: '0 3px 10px rgba(0, 0, 0, 0.08)',
  background: theme.palette.background.paper,
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 5px 15px rgba(0, 0, 0, 0.15)',
  },
}));

const RouteImage = styled(CardMedia)(({ theme }) => ({
  width: '100%',
  maxWidth: '220px',
  height: 'auto',
  margin: '0 auto 15px auto',
  borderRadius: '8px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
}));

const TradeRouteCard = styled(Box)(({ theme }) => ({
  background: '#232a36',
  color: '#c9a74c',
  borderRadius: 16,
  border: '2px solid #2d3642',
  boxShadow: '0 2px 12px rgba(201,167,76,0.08)',
  padding: '32px 0',
  textAlign: 'center',
  fontWeight: 700,
  fontSize: '1.2rem',
  transition: 'all 0.3s',
  cursor: 'pointer',
  '&:hover': {
    background: '#2d3642',
    color: '#fff',
    borderColor: '#c9a74c',
    boxShadow: '0 4px 24px #c9a74c55',
    transform: 'scale(1.05)',
  },
}));

const DarkMapContainer = styled(Box)(({ theme }) => ({
  background: '#181a20',
  borderRadius: 16,
  margin: '32px 0',
  overflow: 'hidden',
  boxShadow: '0 4px 24px #000a',
  '& .leaflet-container': {
    height: '400px',
    width: '100%',
    background: '#181a20',
    filter: 'invert(0.95) hue-rotate(180deg) brightness(0.8)', // darken map tiles
    borderRadius: 16,
  },
}));

const TradeRoutes = () => {
  const { t } = useTranslation();

  const ports = [
    { name: 'Somalia', coords: [2.0469, 45.3182] },
    { name: 'Yemen', coords: [15.3694, 44.1910] },
    { name: 'Oman', coords: [23.5880, 58.3829] },
    { name: 'UAE', coords: [25.276987, 55.296249] },
    { name: 'Pakistan', coords: [24.8615, 66.9905] },
    { name: 'India', coords: [19.0760, 72.8777] },
    { name: 'Kenya', coords: [1.2921, 36.8219] },
  ];

  const latlngs = ports.map(port => port.coords);

  return (
    <RoutesSection id="routes">
      <Container>
        <Typography variant="h2" component="h2" align="center" gutterBottom>
          {t('routes.title')}
        </Typography>
        <Typography paragraph>
          {t('routes.description')}
        </Typography>
        <Grid container spacing={3} justifyContent="center">
          {ports.map((port, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <TradeRouteCard>
                {port.name}
              </TradeRouteCard>
            </Grid>
          ))}
        </Grid>
        <DarkMapContainer>
          <MapContainer center={[10, 55]} zoom={3} scrollWheelZoom={false} style={{ height: 400, width: '100%' }}>
            <TileLayer
              url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
              attribution='&copy; <a href="https://carto.com/attributions">CARTO</a>'
            />
            {ports.map((port, idx) => (
              <Marker key={idx} position={port.coords} icon={L.icon({ iconUrl: 'https://cdn.jsdelivr.net/gh/pointhi/leaflet-color-markers@master/img/marker-icon-gold.png', iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png', shadowSize: [41, 41] })}>
                <Popup>
                  <Typography sx={{ color: '#c9a74c', fontWeight: 700 }}>{port.name}</Typography>
                </Popup>
              </Marker>
            ))}
            <Polyline positions={latlngs} color="#c9a74c" weight={3} opacity={0.7} />
          </MapContainer>
        </DarkMapContainer>
      </Container>
    </RoutesSection>
  );
};

export default TradeRoutes; 